import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';

import { ScrollFreezeServiceTypes } from '@frontend/sports/common/core/feature/scroll-freeze';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { DialogSettings, ModalAnimationHelperService, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { RouteTag } from 'packages/sports/libs/common/core/data-access/route/src/lib/route.models';
import { TimerService } from 'packages/sports/libs/common/core/utils/timer/src/lib/timer.service';

import { DetailedEventViewModel } from '../event-details-common/event-details-common.models';
import { EventDetailsModalComponent } from './event-details-modal.component';

@Injectable({
    providedIn: 'root',
})
export class EventDetailsModalService {
    private modalRef?: ModalRef;
    //keeping fixture id as the updateData() doesn't update the original modal inputs
    private currentFixtureId?: string;
    private previousUrl?: string;

    constructor(
        private modalDialogService: ModalDialogService,
        private routerEventsService: RouterEventsService,
        private timerService: TimerService,
        private location: Location,
        private modalAnimationHelper: ModalAnimationHelperService,
    ) {
        this.routerEventsService.currentActivationStart.pipe(takeUntilDestroyed()).subscribe((route) => {
            // if we navigate away from edp modal, we should close it (betslip links)
            const tag = route?.snapshot.data['tag'] as RouteTag;
            if (!tag || tag !== RouteTag.EventDetails) {
                this.modalRef?.close();
            }
        });
        // if we navigate back from edp modal, we should close it
        window.addEventListener('popstate', () => {
            if (this.previousUrl && window.location.pathname === this.previousUrl) {
                this.modalRef?.close();
            }
        });
    }

    openOrUpdateModal(eventData: DetailedEventViewModel, route: ActivatedRouteSnapshot, urlHistory?: string[]): void {
        if (this.modalRef) {
            if (this.currentFixtureId !== eventData.fixture?.id) {
                this.modalRef.updateData({ detailedEventModel: eventData });
                this.currentFixtureId = eventData.fixture?.id;
            }
        } else {
            this.previousUrl = this.trySetPreviousUrl(urlHistory);
            this.modalRef = this.openModal(eventData, route);
            this.modalRef.result.finally(() => {
                this.modalRef = undefined;
                this.currentFixtureId = undefined;
            });
        }
    }

    close(): void {
        if (this.previousUrl) {
            this.location.replaceState(this.previousUrl);
        }

        this.modalAnimationHelper.addSlideOutHorizontal();
        this.timerService.setTimeoutOutsideAngular(() => {
            this.modalRef?.close();
        }, 350);
    }

    private openModal(detailedEventModel: DetailedEventViewModel, route: ActivatedRouteSnapshot): ModalRef {
        const settings: DialogSettings = {
            isModalDialog: false,
            cssClass: 'modal-popup event-details-modal',
            fit: false,
            showDialogHeader: false,
            closeOnChildPopup: false,
            showBottomNav: true,
            headerSelector: 'event-details-modal',
            gestures: true,
            scrollFreezeType: ScrollFreezeServiceTypes.None,
        };
        return this.modalDialogService.openDialog(EventDetailsModalComponent, { settings, data: { detailedEventModel, route } });
    }

    private trySetPreviousUrl(urlHistory?: string[]): string | undefined {
        const nonEdpUrls = urlHistory?.filter((url) => !url.includes('/events'));
        if (nonEdpUrls && nonEdpUrls.length !== 0) {
            return nonEdpUrls[nonEdpUrls.length - 1];
        }
        return undefined;
    }
}
